import { ArrowDownTrayIcon, ArrowRightIcon, CalculatorIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { PencilIcon } from '@heroicons/react/24/solid';
import {
	GQLDesignPageFragment,
	GQLOperationalSettingsFragment,
	GQLProductFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
	GQLStoreFragment,
} from '@theprintbar/graphql';
import {
	Decoration,
	ImageSize,
	Merch,
	cn,
	getCurrencyString,
	getDecorationContent,
	getDecorationPrice,
	getMaxDecorationColours,
	getSortedColours,
	getSortedDecorationTypes,
	isEMBDec,
	isSCRDec,
	sizedImage,
} from '@theprintbar/shared';
import { useEffect, useRef, useState } from 'react';
import {
	BrandStarAnimated,
	ButtonSize,
	Checkbox,
	DecorationPreview,
	DesignIcon,
	InfoPopup,
	LightningBolt,
	NumberInput,
	OutlineButton,
	RichText,
	WarningButton,
} from '../../..';
import type { CreativeEditorInstance } from '../../CreativeEditor';
import { editorTools } from '../../CreativeEditor/editorTools';
import { ChangeDecorationMethod } from './changeDecorationMethod';

export interface SidePreviewProps {
	editor: CreativeEditorInstance;
	content: GQLDesignPageFragment;
	activeSide: GQLProductSideSideType;
	activeDecoration?: Decoration;
	updatingPrice: boolean;
	product: GQLProductFragment;
	operationalSettings: GQLOperationalSettingsFragment;
	merch?: Merch;
	calculateStitches: () => void;
	openDesigner: (val: boolean) => void;
	switchingType: boolean;
	setMainDecoration: (isMain: boolean) => void;
	setSwitchingType: (switchingType: boolean) => void;
	handleTypeChange: (type: GQLProductSideAvailableDecorationType, available: boolean, colours?: number) => void;
	handleColourChange: (count: number) => void;
	webDomain: string;
	store?: GQLStoreFragment | null;
	crew?: boolean;
	activePanel: 'main' | 'method' | 'colour';
	setActivePanel: (panel: 'main' | 'method' | 'colour') => void;
	freeEdit?: boolean;
}

function SidePreview(props: SidePreviewProps) {
	const {
		editor,
		product,
		content,
		operationalSettings,
		activeSide,
		activeDecoration,
		updatingPrice,
		webDomain,
		merch,
		openDesigner,
		handleColourChange,
		setMainDecoration,
		calculateStitches,
		store,
		crew,
		activePanel,
		setActivePanel,
		freeEdit,
	} = props;
	const side = product.sides.find(s => s.side === activeSide);
	const { error } = activeDecoration || {};
	const [parentHeight, setParentHeight] = useState(0);
	const childRef = useRef<HTMLDivElement>(null);
	const [temporaryDecoration, setTemporaryDecoration] = useState<GQLProductSideAvailableDecorationType | null>(null);
	const [temporaryColourCount, setTemporaryColourCount] = useState<number | null>(null);

	if (!side) return null;
	const available = side.availableDecorations[0];
	const { title, text, icon, bgColour } = getDecorationContent(activeDecoration?.type || available, operationalSettings, product);

	const colourCount = activeDecoration?.meta.colours?.length;
	const maxColours = getMaxDecorationColours(activeDecoration, product);
	const sortedAvailableDecorations = getSortedDecorationTypes(side);
	const fetchEmb = isEMBDec(activeDecoration) && !activeDecoration?.meta.stitches;

	useEffect(() => {
		if (
			!temporaryDecoration &&
			!activeDecoration?.artwork?.size &&
			merch?.decorations.find(d => d.side !== activeSide && d.type !== activeDecoration?.type && !!d.artwork?.size)
		) {
			setActivePanel('method');
		}
	}, [activeSide, activeDecoration]);

	const price = getDecorationPrice({
		settings: operationalSettings,
		decoration: activeDecoration,
		store,
		product,
	});

	useEffect(() => {
		const observer = new ResizeObserver(entries => {
			if (entries[0]) {
				setParentHeight(entries[0].contentRect.height + 20);
			}
		});

		if (childRef.current) {
			observer.observe(childRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [activePanel]); // Update the parent container height whenever the child changes

	return (
		<div className="relative p-4 min-h-96 lg:min-h-56 xl:min-h-48 bg-white overflow-x-hidden" style={{ height: `${parentHeight}px` }}>
			<div
				ref={activePanel === 'main' ? childRef : null}
				className={cn(
					'absolute top-0 left-0 p-4 w-full transition duration-500 ease-in-out translate-x-0',
					activePanel !== 'main' && '-translate-x-full opacity-0',
				)}
			>
				<button
					className="flex flex-col w-full not(:disabled):group"
					disabled={sortedAvailableDecorations.length <= 1 || !freeEdit}
					onClick={() => {
						setActivePanel('method');
						if (activeDecoration?.type) {
							setTemporaryDecoration(activeDecoration.type);
						}
					}}
				>
					<div className="flex items-center justify-between mb-2 ">
						<h5 className="uppercase">Decoration Method</h5>
						{sortedAvailableDecorations.length > 1 && freeEdit && (
							<div className="items-center hidden md:flex">
								<Squares2X2Icon className="w-4 h-4 mr-1" />
								<p className="text-xs">Change decoration method</p>
							</div>
						)}
					</div>
					<div className=" w-full rounded-3xl overflow-hidden bg-white border border-blue">
						<div
							className={cn(
								bgColour,
								'group w-full bg-opacity-20 py-2 px-4 md:py-4 relative flex items-center',
								sortedAvailableDecorations.length > 1 && freeEdit && 'transition hover:bg-opacity-40',
							)}
						>
							{icon && (
								<img
									loading="lazy"
									className="w-10 h-10 md:w-12 md:h-12 mr-4"
									src={sizedImage(icon.image.url, ImageSize.Tiny)}
									alt={icon.altText || `Decoration icon`}
								/>
							)}
							<div className="text-left">
								<h6 className="text-sm md:text-base flex gap-2 items-center">
									{title} {title === 'Digital Printing' && <LightningBolt />}
								</h6>
								<p className="text-xs">{text}</p>
							</div>
							{sortedAvailableDecorations.length > 1 && freeEdit && (
								<ArrowRightIcon className="w-6 h-6 ml-auto group-hover:translate-x-1 transition-transform" />
							)}
						</div>
					</div>
				</button>
				{/* The Mobile preview and button to open the designer */}
				<button
					onClick={() => openDesigner(true)}
					className="relative lg:hidden col-span-full bg-white max-h-96 w-full aspect-square"
				>
					<DecorationPreview
						product={product}
						webDomain={webDomain}
						side={side}
						colourCode={merch?.colour || getSortedColours(product.colours)[0].colour.code}
						decoration={activeDecoration}
					/>
					{!activeDecoration?.artwork?.size ? (
						<div className="absolute inset-0 flex items-center justify-center lg:hidden">
							<div className="p-4 rounded-3xl border border-blue bg-white-pearl space-y-2 flex flex-col items-center">
								<BrandStarAnimated className="w-12 h-12 fill-blue" />
								<h5 className="text-sm">LETS GET DESIGNING!</h5>
								<div className="w-full rounded-full px-4 py-2 text-white flex items-center bg-blue">
									Open Designer
									<DesignIcon className="w-4 h-4 ml-2" />
								</div>
							</div>
						</div>
					) : (
						<div className="rounded-full px-4 py-2 text-white flex items-center bg-blue absolute lg:hidden bottom-2 left-1/2 -translate-x-1/2">
							Edit Design
							<PencilIcon className="w-4 h-4 ml-2" />
						</div>
					)}
				</button>
				<div>
					{!!activeDecoration?.artwork?.size ? (
						<div className="text-xs lg:mt-4 flex items-center justify-between h-full">
							{(isEMBDec(activeDecoration) || isSCRDec(activeDecoration)) && (
								<div className="flex flex-col lg:flex-row items-center lg:space-x-2 justify-between lg:justify-normal">
									<p className="font-bold flex items-center mb-1 lg:mb-0">
										<InfoPopup title="Why are colours limited?" iconClassName="mr-2">
											<RichText
												content={
													isEMBDec(activeDecoration) ? content.embroideryColourInfo : content.screenColourInfo
												}
											/>
										</InfoPopup>
										{isEMBDec(activeDecoration) ? 'Thread Colours' : 'Screens'}
									</p>
									<div className={cn('flex w-20 transition border border-blue rounded-full')}>
										<NumberInput
											hideLabel
											name={`${activeSide}.colours`}
											outsideForm
											noValidate
											value={activeDecoration.meta.requestedColours ?? Math.min(maxColours, colourCount || 1)}
											min={1}
											max={maxColours || 1}
											handleUpdate={newQuantity => handleColourChange(newQuantity)}
											className="w-20"
										/>
									</div>
								</div>
							)}
							{store && (
								<Checkbox
									name="isMainDecoration"
									outsideForm
									label="Main Decoration"
									className="mb-0"
									checked={!!activeDecoration?.isMainDecoration}
									onChange={({ currentTarget }) => setMainDecoration(currentTarget.checked)}
								/>
							)}

							{fetchEmb ? (
								<div className={cn('text-center', !!error && 'text-red')}>
									<WarningButton
										size={ButtonSize.XS}
										className="w-full"
										disabled={!!error || updatingPrice}
										onClick={calculateStitches}
										icon={CalculatorIcon}
									>
										{error ? 'Error in design' : updatingPrice ? 'Calculating...' : 'Calculate Pricing'}
									</WarningButton>
								</div>
							) : (
								<>
									<div className="flex flex-col items-end justify-end">
										<div className="flex items-center">
											<p className="col-span-2 text-right mr-1.5">{`+${getCurrencyString(price)} per item`}</p>
											<InfoPopup title="What is the decoration price?">
												<RichText content={content.decorationPriceInfo} />
											</InfoPopup>
										</div>
										{!store && activeDecoration && !!price && (
											<InfoPopup
												title="Volume Discounts"
												className="italic underline"
												iconClassName="hidden"
												buttonText="Volume discounts available"
											>
												<RichText
													content={
														activeDecoration?.type === GQLProductSideAvailableDecorationType.Emb
															? content.embroideryVolumeInfo
															: activeDecoration?.type === GQLProductSideAvailableDecorationType.Scr
															? content.screenVolumeInfo
															: activeDecoration?.type === GQLProductSideAvailableDecorationType.Dtf
															? content.dtfVolumeInfo
															: content.dtgVolumeInfo
													}
												/>
											</InfoPopup>
										)}
									</div>
								</>
							)}
							{crew && (
								<OutlineButton
									className="ml-auto mt-2"
									size={ButtonSize.S}
									icon={ArrowDownTrayIcon}
									onClick={() => editorTools.downloadVectorPDF(editor)}
								>
									Download Vector PDF
								</OutlineButton>
							)}
						</div>
					) : (
						<div className="hidden md:block mt-4">
							<h5 className="text-xs uppercase">Need help getting started?</h5>
							<p className="text-xs">Check out the tutorial button on the left for more info.</p>
						</div>
					)}
				</div>
			</div>
			<ChangeDecorationMethod
				{...props}
				side={side}
				childRef={childRef}
				temporaryDecoration={temporaryDecoration}
				setTemporaryColourCount={setTemporaryColourCount}
				setTemporaryDecoration={setTemporaryDecoration}
				temporaryColourCount={temporaryColourCount}
			/>
		</div>
	);
}

export default SidePreview;
